import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CenterModal from "../../UI/Modal/CenterModal";
import useLanguageChange from "../../../hooks/useLanguageChange";
import { GLOBAL_MEETING_DATA, PublicVariables } from "../../../constants/PublicVariables";
import { setIsConfirmVideoModalOpen, setNotAllowedCamera, setVideoError } from "../../../reducers/twilioEvents-reducer";
import { HELP_ARTICLE } from "../../../constants/LDConstants";
import { goToPlayStore } from "../../../shared/utility";
import { setIsCallSettingDrawerOpen, setIsStartCameraAsPerAgentRequestPermission } from "../../../reducers/common-reducer";
import { ERROR_TYPE } from "../../../constants/CallSettingConstants";
import { performStartStopVideo } from "./VideoUtils";
import { handleEndCallFunc } from "../Room/roomFunction";
import { setPermissionPopupText } from "../../CommonModals/CommonUtils";

const VideoPermissionPopup = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();
    const [isCameraPermissionPopup, setIsCameraPermissionPopup] = useState(false);
    const [isCameraDeviceNotFoundPopup, setIsCameraDeviceNotFoundPopup] = useState(false);
    const [isNotReadablePopup, setIsNotReadablePopup] = useState(false);
    const { notAllowedCamera, videoError, isConfirmVideoModalOpen, isLocalUserScreenSharing, isOpponentUserScreenSharing } = useSelector(
        (state) => state.twilioEvents
    );

    useEffect(() => {
        if (notAllowedCamera === true && videoError === ERROR_TYPE.PERMISSION) {
            setIsCameraPermissionPopup(true);
        }
        if (notAllowedCamera === true && videoError === ERROR_TYPE.NOT_FOUND) {
            setIsCameraDeviceNotFoundPopup(true);
        }
        if (notAllowedCamera === true && [ERROR_TYPE.CAM_IN_USE, ERROR_TYPE.NOT_READABLE, ERROR_TYPE.UNKNOWN].includes(videoError)) {
            setIsNotReadablePopup(true);
        }
    }, [videoError, notAllowedCamera]);

    //#region permssion popup

    const handleClosePermissionModal = () => {
        setIsCameraPermissionPopup(false);
        dispatch(setVideoError(null));
        dispatch(setNotAllowedCamera(false));
    };

    const handleReloadClick = () => {
        handleEndCallFunc();
        setTimeout(() => {
            window.location.reload();
        }, 500);
    };

    const permissionHeader = useMemo(() => {
        return (
            <>
                <h5 className='modal-title'>{localisedValues.camera_permission_required_title}</h5>
                <button type='button' className='btn-close' aria-label='Close' onClick={() => handleClosePermissionModal()}>
                    <span className='btn-icon icon-close-light'></span>
                </button>
            </>
        );
    }, [isCameraPermissionPopup]);

    const permissionBody = useMemo(() => {
        return (
            <div className='cms-modal-text' data-testid='camera-permissionPop-model'>
                <p>
                    <span>{localisedValues.camera_permission_required_disc1}</span>
                    <button className='btn default-btn-link' onClick={handleReloadClick}>
                        {localisedValues.click_here_label}
                    </button>
                    <span>&nbsp;{localisedValues.reload_page_disc}</span>
                </p>
                {GLOBAL_MEETING_DATA.isAgent && PublicVariables.isMobile && (
                    <p>
                        <strong>{localisedValues.note_colon}</strong>{" "}
                        <span>{localisedValues.host_features_will_not_be_available_when_using_app}</span>
                    </p>
                )}
            </div>
        );
    }, [isCameraPermissionPopup]);

    const permissionFooter = useMemo(() => {
        return (
            <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
                <div className='button'>
                    <button className='btn default-btn-link' onClick={() => handleClosePermissionModal()}>
                        {localisedValues.close}
                    </button>
                </div>
                <div className='button'>
                    <button className='btn default-btn-primary' onClick={() => window.open(HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK)}>
                        {localisedValues.view_help_article}
                    </button>
                </div>
            </div>
        );
    }, [isCameraPermissionPopup]);
    //#endregion permission popup

    //#region camera device not found pop up

    const handleCloseDeviceNotFoundModal = () => {
        setIsCameraDeviceNotFoundPopup(false);
        dispatch(setVideoError(null));
        dispatch(setNotAllowedCamera(false));
    };

    const handleMobileAppButton = () => {
        handleEndCallFunc();
        goToPlayStore();
        handleCloseDeviceNotFoundModal();
    };

    const customText = useMemo(() => {
        return setPermissionPopupText();
    }, [isCameraDeviceNotFoundPopup]);

    const deviceNotFoundHeader = useMemo(() => {
        return (
            <>
                <h5 className='modal-title'>{localisedValues.camera_not_found}</h5>
                <button type='button' className='btn-close' aria-label='Close' onClick={() => handleCloseDeviceNotFoundModal()}>
                    <span className='btn-icon icon-close-light'></span>
                </button>
            </>
        );
    }, [isCameraDeviceNotFoundPopup]);

    const deviceNotFoundBody = useMemo(() => {
        return (
            <div className='cms-modal-text' data-testid='device-notFound-model'>
                {!PublicVariables.isMobile && <p>{localisedValues.enable_or_attach_camera_and_reconnect_call}</p>}
                {PublicVariables.isMobile && (
                    <>
                        <p>
                            {localisedValues.we_cannot_access_your_camera} <span dangerouslySetInnerHTML={customText}></span>{" "}
                        </p>

                        <p>
                            <span>{localisedValues.still_facing_issue}</span> <span>{localisedValues.try_the}</span>{" "}
                            <button className='btn default-btn-link' onClick={() => handleMobileAppButton()}>
                                {localisedValues.mobile_app}
                            </button>
                        </p>

                        {GLOBAL_MEETING_DATA.isAgent && (
                            <p>
                                <strong>{localisedValues.note_colon}</strong>{" "}
                                <span>{localisedValues.host_features_will_not_be_available_when_using_app}</span>
                            </p>
                        )}
                    </>
                )}
            </div>
        );
    }, [isCameraDeviceNotFoundPopup]);

    const deviceNotFoundFooter = useMemo(() => {
        return (
            <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
                <div className='button'>
                    <button className='btn default-btn-link' onClick={() => handleCloseDeviceNotFoundModal()}>
                        {localisedValues.close}
                    </button>
                </div>
                {PublicVariables.isMobile && (
                    <div className='button'>
                        <button className='btn default-btn-primary' onClick={() => window.open(HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK)}>
                            {localisedValues.view_help_article}
                        </button>
                    </div>
                )}
            </div>
        );
    }, [isCameraDeviceNotFoundPopup]);

    //#endregion camera device not found pop up

    //#region permssion popup

    const handleCloseNotReadableModal = () => {
        setIsNotReadablePopup(false);
        dispatch(setVideoError(null));
        dispatch(setNotAllowedCamera(false));
    };

    const handleCallParticipantButton = () => {
        setIsNotReadablePopup(false);
        dispatch(setNotAllowedCamera(false));
        dispatch(setIsCallSettingDrawerOpen(true));
        document.getElementById("callSettingsDrawerDiv")?.click();
    };

    const notReadablePopupHeader = useMemo(() => {
        return (
            <>
                <h5 className='modal-title'>{localisedValues.cannot_start_video}</h5>
                <button type='button' className='btn-close' aria-label='Close' onClick={() => handleCloseNotReadableModal()}>
                    <span className='btn-icon icon-close-light'></span>
                </button>
            </>
        );
    }, [isNotReadablePopup]);

    const notReadablePopupBody = useMemo(() => {
        return (
            <div className='cms-modal-text' data-testid='not-readablepop-model'>
                <p>
                    <span>{localisedValues.failed_to_start_video_camera}</span>{" "}
                    <span>{localisedValues.please_select_another_camera_from_call_settings}</span>
                </p>
            </div>
        );
    }, [isNotReadablePopup]);

    const notReadablePopupFooter = useMemo(() => {
        return (
            <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
                <div className='button'>
                    <button className='btn default-btn-link' onClick={() => handleCloseNotReadableModal()}>
                        {localisedValues.okay_initcap}
                    </button>
                </div>
                <div className='button'>
                    <button className='btn default-btn-primary' onClick={() => handleCallParticipantButton()}>
                        {localisedValues.call_settings}
                    </button>
                </div>
            </div>
        );
    }, [isNotReadablePopup]);
    //#endregion permission popup

    // <!-- override video - screen share Pop-up -->
    const handleCloseOverrideModal = () => {
        dispatch(setIsConfirmVideoModalOpen(false));
        dispatch(setIsStartCameraAsPerAgentRequestPermission(false));
    };

    const handleConfirmOverrideModal = () => {
        performStartStopVideo(true);
        dispatch(setIsConfirmVideoModalOpen(false));
    };

    const overrideVideoHeader = (
        <>
            <h5 className='modal-title'>{localisedValues["share_live_video"]}</h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={handleCloseOverrideModal}>
                <span className='btn-icon icon-close-light'></span>
            </button>
        </>
    );

    const overrideVideoBody = useMemo(() => {
        let bodyText = localisedValues["override_video"];

        if (!isLocalUserScreenSharing && !isOpponentUserScreenSharing) {
            bodyText = localisedValues["override_video"];
        } else if (isOpponentUserScreenSharing || isLocalUserScreenSharing) {
            bodyText = localisedValues["override_screen_share"];
        }

        return (
            <div className='cms-modal-text'>
                <p>{bodyText}</p>
            </div>
        );
    }, [localisedValues, isLocalUserScreenSharing, isOpponentUserScreenSharing]);

    const overrideVideoFooter = (
        <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
            <div className='button'>
                <button className='btn default-btn-link' onClick={handleCloseOverrideModal}>
                    {localisedValues["cancel_initcap"]}
                </button>
            </div>
            <div className='button'>
                <button className='btn default-btn-primary' onClick={handleConfirmOverrideModal}>
                    {localisedValues["yes_initcap"]}
                </button>
            </div>
        </div>
    );

    return (
        <>
            <CenterModal
                showModal={isCameraDeviceNotFoundPopup}
                closeModal={() => handleCloseDeviceNotFoundModal()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={deviceNotFoundHeader}
                body={deviceNotFoundBody}
                footer={deviceNotFoundFooter}
                backdrop={"static"}
            />

            <CenterModal
                showModal={isCameraPermissionPopup}
                closeModal={() => handleClosePermissionModal()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={permissionHeader}
                body={permissionBody}
                footer={permissionFooter}
                backdrop={"static"}
            />

            <CenterModal
                showModal={isNotReadablePopup}
                closeModal={() => handleCloseNotReadableModal()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={notReadablePopupHeader}
                body={notReadablePopupBody}
                footer={notReadablePopupFooter}
                backdrop={"static"}
            />

            <CenterModal
                showModal={isConfirmVideoModalOpen}
                closeModal={handleCloseOverrideModal}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={overrideVideoHeader}
                body={overrideVideoBody}
                footer={overrideVideoFooter}
                backdrop={"static"}
            />
        </>
    );
};

export default VideoPermissionPopup;
